import { useState } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

import { useAuthStore } from '@/features/auth/model/auth-store';
import type { MagicLinkFormData } from '@/features/auth/model/schemas';

import { authService } from '@/shared/api';
import { OtpSendError } from '@/shared/api/auth-service';
import { teamService } from '@/shared/api/team-service';
import { ROUTES } from '@/shared/config/routes';
import { useOtpCooldown } from '@/shared/hooks/use-otp-cooldown';
import { analytics } from '@/shared/lib/analytics';
import { logger } from '@/shared/lib/logger';
import { isOnboarded } from '@/shared/lib/onboarding-flag';
import {
  classifyOtpSendError,
  DEFAULT_COOLDOWN_SECONDS,
  getOtpCooldownRemaining,
} from '@/shared/lib/otp-cooldown';

type AuthStep = 'email' | 'code';

type UseAuthFlowReturn = {
  // State
  step: AuthStep;
  userEmail: string;
  isCodeError: boolean;
  isVerifying: boolean;
  isResending: boolean;
  isSubmitting: boolean;

  // Countdown
  timeLeft: number;
  canResend: boolean;

  // Actions
  submitEmail: (data: MagicLinkFormData) => Promise<void>;
  submitCode: (code: string) => Promise<void>;
  resendCode: () => Promise<void>;
  goBackToEmail: () => void;
};

export const useAuthFlow = (): UseAuthFlowReturn => {
  const navigate = useNavigate();
  const login = useAuthStore((state) => state.login);
  const logout = useAuthStore((state) => state.logout);

  const [step, setStep] = useState<AuthStep>('email');
  const [userEmail, setUserEmail] = useState('');
  const [isCodeError, setIsCodeError] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Resend cooldown for the active email. Persisted in localStorage (see
  // otp-cooldown.ts) so a page reload or a back-to-email round-trip can't reset
  // it and let an impatient user re-fire into Supabase's server-side limit —
  // which is exactly the 429 spam loop that lost us a signup (see
  // docs/runbooks/2026-05-18-auth-supabase-rebranding.md). Keyed by `userEmail`
  // for the code-step display + resend gate; the email-submit path writes via
  // the module fns with the explicit email (userEmail updates asynchronously),
  // then setUserEmail re-hydrates this hook for the code-step countdown.
  const { secondsLeft, isCoolingDown, startCooldown, clear: clearCooldown } =
    useOtpCooldown(userEmail);

  // Map a failed `sendEmailOTP` to user-actionable copy. Distinguishes the
  // per-email cooldown (short, exact seconds) from the project-wide hourly cap
  // (long) from generic failures — instead of one vague "wait a minute".
  const handleOtpSendFailure = (error: unknown) => {
    const outcome =
      error instanceof OtpSendError
        ? classifyOtpSendError({
            status: error.status,
            code: error.code,
            message: error.message,
          })
        : ({ kind: 'generic' } as const);

    if (outcome.kind === 'generic') {
      toast.error("Couldn't send the code. Check your connection and try again.", {
        id: 'otp-send-error',
      });
    } else if (outcome.kind === 'invalid_email') {
      toast.error(
        "That email address can't receive codes — double-check it for typos and try again.",
        { id: 'otp-send-error' },
      );
    } else if (outcome.kind === 'hourly_cap') {
      toast.error(
        "We've sent too many codes to this address in the last hour. Your most recent code still works — check your inbox and spam, or try again later.",
        { id: 'otp-hourly-cap' },
      );
    } else {
      toast.error(`Please wait ${outcome.seconds}s before requesting another code.`, {
        id: 'otp-cooldown',
      });
    }

    logger.auth.error('Failed to send OTP', { error });
    return outcome;
  };

  const submitEmail = async (data: MagicLinkFormData) => {
    const email = data.email.trim();
    setUserEmail(email);

    // Already inside the per-email cooldown (e.g. they reloaded and re-submitted)
    // — don't spend another server send. Route them to the code they already
    // have and tell them how long until they can request a fresh one.
    const remaining = getOtpCooldownRemaining(email);
    if (remaining > 0) {
      startCooldown(remaining, email);
      setStep('code');
      toast.info(
        `A code is already on its way to ${email}. Check your inbox and spam — you can request another in ${remaining}s.`,
        { id: 'otp-cooldown' },
      );
      return;
    }

    setIsSubmitting(true);
    try {
      await authService.sendEmailOTP(email);
      // Re-arm via the hook with the explicit email. userEmail (the hook's key)
      // hasn't settled this tick, so pass it explicitly — otherwise the code-step
      // countdown reads 0 and "Resend" goes live immediately (spam loop).
      startCooldown(DEFAULT_COOLDOWN_SECONDS, email);
      setStep('code');
      toast.success('Verification code sent to your email!', { id: 'email-otp-success' });
    } catch (error) {
      const outcome = handleOtpSendFailure(error);
      // A rate-limit means a code was already sent on an earlier attempt — show
      // the code entry and persist the real wait so the gate survives a reload.
      if (outcome.kind === 'cooldown' || outcome.kind === 'hourly_cap') {
        startCooldown(outcome.seconds, email);
        setStep('code');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitCode = async (code: string) => {
    setIsCodeError(false);
    setIsVerifying(true);

    try {
      await authService.verifyEmailOTP(userEmail, code);

      // Verified — the resend cooldown is moot, drop it so a future sign-in for
      // this address isn't gated by a stale timer.
      clearCooldown();

      // Supabase automatically stores the session tokens
      // No need for manual localStorage operations

      // Register profile (upsert — safe for existing users)
      // Must happen before getMe so profile exists for team creation
      await authService.registerOAuthUser();

      // Now fetch actual user data from /auth/me
      const userResponse = await authService.getMe();

      // Check if user is authenticated according to backend
      if (userResponse.data.authenticated === false) {
        // Session exists but backend says not authenticated
        logout();
        toast.error('Authentication expired. Please log in again.', { id: 'auth-expired' });
        navigate(ROUTES.AUTH.REGISTER);
        return;
      }

      // Check if we have user data (authenticated response)
      if ('id' in userResponse.data) {
        // Login with real user data (Supabase already has the tokens)
        login(userResponse.data);

        // Identify user in RudderStack and track registration
        // Wrapped in try-catch to ensure analytics errors don't disrupt auth flow
        try {
          analytics.identify(userResponse.data.id, { email: userResponse.data.email });

          // Track User Registered event only for new signups
          if (userResponse.data.justSignUp) {
            analytics.track('User Registered', {
              email: userResponse.data.email,
              userId: userResponse.data.id,
            });
          }
        } catch (analyticsError) {
          logger.analytics.error('Failed to track auth event', { error: analyticsError });
        }
      } else {
        // No user data in response - logout
        logout();
        toast.error('Authentication failed. Please log in again.', { id: 'auth-failed' });
        navigate(ROUTES.AUTH.REGISTER);
        return;
      }

      // Check for pending invite token
      const pendingToken = sessionStorage.getItem('pendingInviteToken');
      if (pendingToken) {
        sessionStorage.removeItem('pendingInviteToken');
        try {
          await teamService.acceptInvite(pendingToken);
          navigate('/dashboard/team');
          return;
        } catch {
          // Invite acceptance failed, continue normal flow
        }
      }

      // Same routing rule as the OAuth callback + ProtectedRoute: an
      // onboarded user is one with `onboarded_at` on the server profile OR
      // the `onboarding.completedWizard` localStorage flag set by the wizard.
      // The old `hasTeam` check would never fire because auth.service.register()
      // auto-creates a team on every login, so it always reported true.
      if (isOnboarded(userResponse.data)) {
        navigate('/dashboard');
      } else {
        navigate('/onboarding');
      }
    } catch (error) {
      setIsCodeError(true);
      setIsVerifying(false);
      const errorMessage =
        error instanceof Error ? error.message : 'Invalid verification code. Please try again.';
      toast.error(errorMessage, { id: 'code-verification-error' });
    }
  };

  const resendCode = async () => {
    if (isCoolingDown || isResending) return;

    setIsResending(true);
    try {
      await authService.sendEmailOTP(userEmail);
      startCooldown(DEFAULT_COOLDOWN_SECONDS, userEmail);
      toast.success('Verification code sent to your email!', { id: 'resend-otp-success' });
    } catch (error) {
      const outcome = handleOtpSendFailure(error);
      if (outcome.kind === 'cooldown' || outcome.kind === 'hourly_cap') {
        startCooldown(outcome.seconds, userEmail);
      }
    } finally {
      setIsResending(false);
    }
  };

  const goBackToEmail = () => {
    setStep('email');
    setUserEmail('');
    setIsCodeError(false);
    // Intentionally do NOT clear the persisted cooldown — returning to the email
    // screen must not reset the server-side resend window.
  };

  return {
    // State
    step,
    userEmail,
    isCodeError,
    isVerifying,
    isResending,
    isSubmitting,

    // Countdown
    timeLeft: secondsLeft,
    canResend: !isCoolingDown,

    // Actions
    submitEmail,
    submitCode,
    resendCode,
    goBackToEmail,
  };
};
